import { condString } from '@silverhand/essentials';

export const searchKeysCamelCase = Object.freeze(['organizationId', 'appId', 'uiLocales'] as const);

type SearchKeysCamelCase = (typeof searchKeysCamelCase)[number];

export const searchKeys = Object.freeze({
  /**
   * The key for specifying the organization ID that may be used to override the default settings.
   */
  organizationId: 'organization_id',
  /**
   * The current application ID.
   */
  appId: 'app_id',
  /**
   * The end-user's preferred languages, presented as a space-separated list of BCP47 language tags.
   * E.g. `en` or `en-US` or `en-US en`.
   */
  uiLocales: 'ui_locales',
} satisfies Record<SearchKeysCamelCase, string>);

export const handleSearchParametersData = () => {
  const { search } = window.location;

  if (!search) {
    return;
  }

  const parameters = new URLSearchParams(search);

  // Keys that stay in the URL. app_id is kept for resuming sessions. organization_id is kept so a
  // refresh keeps the organization's branding: once the page has stripped a key, a reload of the
  // remaining URL (which still has app_id) cannot tell "refresh" from "new sign-in without an
  // organization", and the loop below would clear it.
  const keptInUrl = new Set<string>([searchKeys.appId, searchKeys.organizationId]);

  // Store known search keys to the session storage and remove the rest from the URL to keep it
  // clean.
  for (const key of Object.values(searchKeys)) {
    const value = parameters.get(key);
    if (value) {
      sessionStorage.setItem(key, value);
      if (!keptInUrl.has(key)) {
        parameters.delete(key);
      }
    } else if (key !== searchKeys.appId) {
      sessionStorage.removeItem(key);
    }
  }

  // `toString()` rather than `size`: equivalent, and `URLSearchParams.size` is missing in older
  // browsers (and jsdom), where it silently dropped the whole query, app_id included.
  const remaining = parameters.toString();
  const conditionalParamString = condString(remaining && `?${remaining}`);

  // Preserve the existing history state (e.g. React Router's location state) to avoid
  // losing in-progress flow data (like Continue page's interactionEvent) on page refresh.
  window.history.replaceState(
    window.history.state,
    '',
    window.location.pathname + conditionalParamString
  );
};
